/**
 * Payment schemas for SaleStore
 *
 * This module provides comprehensive payment tracking with:
 * - Discriminated unions for type-safe payment handling
 * - Separate tracking for pending, completed, failed, and refunded payments
 * - Card-specific details for all card transactions
 * - Failure tracking with detailed error information
 * - Refund tracking with partial refund support
 *
 * @see SALE_VS_CHECKOUT_COMPARISON.md for architecture details
 */
import { z } from 'zod';
import { PaymentMethodDataSchema } from '../customer/payment-methods';
import { ActionStatusEnum, PaymentMethodEnum } from '../status';
/* ────────────────────────────────────────────────────────────────
   1. Base Payment Transaction Schema
   ──────────────────────────────────────────────────────────────── */
/**
 * BasePaymentTxSchema
 *
 * Core fields shared by all payment transactions regardless of method or status.
 * This serves as the foundation for all payment types.
 *
 * @property id - Unique transaction identifier
 * @property method - Payment method (CASH, CARD, EBT, etc.)
 * @property amount - Transaction amount in dollars
 * @property merchantNetAmount - Net amount merchant receives after fees
 * @property date - Transaction timestamp
 * @property status - Current transaction status
 */
export const BasePaymentTxSchema = z.object({
    id: z.string(),
    method: z.nativeEnum(PaymentMethodEnum),
    amount: z.number().positive(),
    merchantNetAmount: z.number(),
    date: z.date(),
    status: z.nativeEnum(ActionStatusEnum),
});
/* ────────────────────────────────────────────────────────────────
   2. Card Payment Details
   ──────────────────────────────────────────────────────────────── */
/**
 * CardPaymentDetailsSchema
 *
 * Extended details for card-based payments (credit, debit, EBT).
 * Captures card information for receipt generation and reconciliation.
 *
 * @property last4CardNumber - Last 4 digits of card number (PCI compliance)
 * @property cardType - Card brand (Visa, Mastercard, Amex, etc.)
 * @property cardHolderName - Name on card (optional, for receipts)
 * @property authCode - Authorization code from payment processor
 * @property transactionId - Processor-specific transaction ID
 */
export const CardPaymentDetailsSchema = z.object({
    last4CardNumber: z.string().length(4),
    cardType: z.string(),
    cardHolderName: z.string().optional().nullable(),
    authCode: z.string().optional().nullable(),
    transactionId: z.string().optional().nullable(),
});
/* ────────────────────────────────────────────────────────────────
   3. Payment Failure Details
   ──────────────────────────────────────────────────────────────── */
/**
 * PaymentFailureDetailsSchema
 *
 * Detailed failure information for debugging and customer support.
 * Helps merchants understand why payments fail and how to resolve them.
 *
 * @property failureReason - Human-readable failure reason
 * @property failureCode - Machine-readable error code from processor
 * @property failedAt - Timestamp when failure occurred
 * @property retryable - Whether transaction can be retried
 * @property processorMessage - Raw message from payment processor
 */
export const PaymentFailureDetailsSchema = z.object({
    failureReason: z.string(),
    failureCode: z.string(),
    failedAt: z.date(),
    retryable: z.boolean().default(true),
    processorMessage: z.string().optional().nullable(),
});
/* ────────────────────────────────────────────────────────────────
   4. Refund Details
   ──────────────────────────────────────────────────────────────── */
/**
 * RefundDetailsSchema
 *
 * Tracks refund information for both full and partial refunds.
 * Links refunds back to original payments for audit trails.
 *
 * @property refundId - Unique refund transaction ID
 * @property refundedAmount - Amount refunded (may be less than original)
 * @property refundedAt - Timestamp when refund was processed
 * @property refundReason - Merchant-provided reason for refund
 * @property originalPaymentId - Links back to the original payment
 * @property isPartialRefund - Whether this is a partial refund
 */
export const RefundDetailsSchema = z.object({
    refundId: z.string(),
    refundedAmount: z.number().positive(),
    refundedAt: z.date(),
    refundReason: z.string().optional().nullable(),
    originalPaymentId: z.string(),
    isPartialRefund: z.boolean().default(false),
});
/* ────────────────────────────────────────────────────────────────
   5. Discriminated Payment Types
   ──────────────────────────────────────────────────────────────── */
/**
 * CardPaymentTxSchema
 *
 * Complete card payment transaction with card details.
 * Used for pending, completed, and failed card payments.
 * Discriminated by method being CARD or EBT.
 */
export const CardPaymentTxSchema = BasePaymentTxSchema.extend({
    method: z.enum([
        PaymentMethodEnum.CARD,
        PaymentMethodEnum.EBT,
        PaymentMethodEnum.MANUAL_CARD,
        PaymentMethodEnum.BLE_CONNECTED_READER,
        PaymentMethodEnum.WEB_PAYMENT,
        PaymentMethodEnum.RECURRING_PAYMENT,
        PaymentMethodEnum.PAYMENT_LINK,
        PaymentMethodEnum.MOBILE_APP_PAYMENT,
    ]),
    cardDetails: CardPaymentDetailsSchema,
    paymentMethod: PaymentMethodDataSchema.optional(),
});
/**
 * NonCardPaymentTxSchema
 *
 * Payment transactions for non-card methods (cash, check, etc.).
 * These don't require card details but follow same base structure.
 * Discriminated by method being anything except CARD or EBT.
 */
export const NonCardPaymentTxSchema = BasePaymentTxSchema.extend({
    method: z.enum([
        PaymentMethodEnum.CASH,
        PaymentMethodEnum.CHECK,
        PaymentMethodEnum.DEBT,
        PaymentMethodEnum.BNPL,
        PaymentMethodEnum.OTHER,
    ]),
});
// union of all payment transaction types from the two schemas above
/**
 * AllPaymentTxMethodSchema
 *
 * Discriminated union of all payment transaction types.
 */
export const AllPaymentTxMethodSchema = z.discriminatedUnion('method', [
    CardPaymentTxSchema,
    NonCardPaymentTxSchema,
]);
/**
 * FailedPaymentTxSchema
 *
 * Payment transaction that failed, with failure details.
 * Can be either card or non-card payment type.
 * Status must be FAILED or ERROR.
 */
export const FailedPaymentTxSchema = z.discriminatedUnion('method', [
    CardPaymentTxSchema.extend({
        status: z.literal(ActionStatusEnum.FAILED),
        failureDetails: PaymentFailureDetailsSchema,
    }),
    NonCardPaymentTxSchema.extend({
        status: z.literal(ActionStatusEnum.FAILED),
        failureDetails: PaymentFailureDetailsSchema,
    }),
]);
/**
 * RefundedPaymentTxSchema
 *
 * Payment transaction that has been refunded (fully or partially).
 * Original payment must have been successful before refunding.
 * Status must be REFUNDED or PARTIALLY_REFUNDED.
 */
export const RefundedPaymentTxSchema = z.discriminatedUnion('method', [
    CardPaymentTxSchema.extend({
        status: z.enum([
            ActionStatusEnum.REFUNDED,
            ActionStatusEnum.PARTIALLY_REFUNDED,
        ]),
        refundDetails: RefundDetailsSchema,
    }),
    NonCardPaymentTxSchema.extend({
        status: z.enum([
            ActionStatusEnum.REFUNDED,
            ActionStatusEnum.PARTIALLY_REFUNDED,
        ]),
        refundDetails: RefundDetailsSchema,
    }),
]);
/**
 * CompletedPaymentTxSchema
 *
 * Successfully completed payment transaction.
 * Status must be SUCCESS.
 * Card payments include card details for receipt generation.
 */
export const CompletedPaymentTxSchema = z.discriminatedUnion('method', [
    CardPaymentTxSchema.extend({
        status: z.literal(ActionStatusEnum.SUCCESS),
    }),
    NonCardPaymentTxSchema.extend({
        status: z.literal(ActionStatusEnum.SUCCESS),
    }),
]);
/**
 * PendingPaymentTxSchema
 *
 * Payment transaction currently being processed.
 * Status must be PENDING or PROCESSING.
 * Card payments include card details even while pending.
 */
export const PendingPaymentTxSchema = z.discriminatedUnion('method', [
    CardPaymentTxSchema.extend({
        status: z.literal(ActionStatusEnum.PENDING),
    }),
    NonCardPaymentTxSchema.extend({
        status: z.literal(ActionStatusEnum.PENDING),
    }),
]);
/* ────────────────────────────────────────────────────────────────
   6. Payment Info Schema
   ──────────────────────────────────────────────────────────────── */
/**
 * SalePaymentSchema
 *
 * Complete payment information for a sale, with payments organized by status.
 * This replaces the previous schema that only tracked pending/completed.
 *
 * Breaking Changes from Previous Schema:
 * - Added: failedPayments array for tracking payment failures
 * - Added: refundedPayments array for tracking refunds
 * - Updated: pendingPayments now uses PendingPaymentTxSchema (includes card details)
 * - Updated: completedPayments now uses CompletedPaymentTxSchema (discriminated union)
 * - Maintained: totalAmount for quick reference
 *
 * @property pendingPayments - Payments currently being processed
 * @property completedPayments - Successfully completed payments
 * @property failedPayments - Failed payment attempts with failure details
 * @property refundedPayments - Refunded payments (full or partial)
 * @property totalAmount - Sum of all completed payments (excludes refunds)
 */
export const SalePaymentSchema = z.object({
    pendingPayments: z.array(PendingPaymentTxSchema),
    completedPayments: z.array(CompletedPaymentTxSchema),
    failedPayments: z.array(FailedPaymentTxSchema),
    refundedPayments: z.array(RefundedPaymentTxSchema),
    totalAmount: z.number(),
});
/* ────────────────────────────────────────────────────────────────
   7. Payment Helper Utilities
   ──────────────────────────────────────────────────────────────── */
/**
 * Payment helper utilities for common operations
 */
/**
 * Calculate total amount from completed payments (excluding refunds)
 *
 * @param completedPayments - Array of completed payment transactions
 * @returns Total amount successfully collected
 */
export function calculateTotalAmount(completedPayments) {
    return completedPayments.reduce((sum, payment) => sum + payment.amount, 0);
}
/**
 * Calculate total refunded amount
 *
 * @param refundedPayments - Array of refunded payment transactions
 * @returns Total amount refunded to customer
 */
export function calculateTotalRefunded(refundedPayments) {
    return refundedPayments.reduce((sum, payment) => {
        // Type guard to ensure we're accessing refundDetails safely
        if ('refundDetails' in payment) {
            return sum + payment.refundDetails.refundedAmount;
        }
        return sum;
    }, 0);
}
/**
 * Calculate net payment amount (completed - refunded)
 *
 * @param paymentInfo - Complete payment information
 * @returns Net amount collected from customer
 */
export function calculateNetPaymentAmount(paymentInfo) {
    const completed = calculateTotalAmount(paymentInfo.completedPayments);
    const refunded = calculateTotalRefunded(paymentInfo.refundedPayments);
    return completed - refunded;
}
/**
 * Check if a payment is a card payment
 *
 * @param payment - Any payment transaction
 * @returns True if payment method is CARD or EBT
 */
export function isCardPayment(payment) {
    return (payment.method === PaymentMethodEnum.CARD ||
        payment.method === PaymentMethodEnum.MANUAL_CARD ||
        payment.method === PaymentMethodEnum.BLE_CONNECTED_READER ||
        payment.method === PaymentMethodEnum.EBT ||
        payment.method === PaymentMethodEnum.WEB_PAYMENT ||
        payment.method === PaymentMethodEnum.RECURRING_PAYMENT ||
        payment.method === PaymentMethodEnum.PAYMENT_LINK);
}
/**
 * Get all card payments regardless of status
 *
 * @param paymentInfo - Complete payment information
 * @returns Array of all card payments (various status types)
 */
export function getAllCardPayments(paymentInfo) {
    const allPayments = [
        ...paymentInfo.pendingPayments,
        ...paymentInfo.completedPayments,
        ...paymentInfo.failedPayments,
        ...paymentInfo.refundedPayments,
    ];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return allPayments.filter(isCardPayment);
}
/**
 * Get payment breakdown by method
 *
 * @param completedPayments - Array of completed payment transactions
 * @returns Object with totals for each payment method
 */
export function getPaymentBreakdown(completedPayments) {
    return completedPayments.reduce((acc, payment) => {
        switch (payment.method) {
            case PaymentMethodEnum.CASH:
                acc.cash += payment.amount;
                break;
            case PaymentMethodEnum.CARD:
            case PaymentMethodEnum.MANUAL_CARD:
            case PaymentMethodEnum.BLE_CONNECTED_READER:
            case PaymentMethodEnum.WEB_PAYMENT:
            case PaymentMethodEnum.RECURRING_PAYMENT:
            case PaymentMethodEnum.PAYMENT_LINK:
                acc.card += payment.amount;
                break;
            case PaymentMethodEnum.EBT:
                acc.ebt += payment.amount;
                break;
            case PaymentMethodEnum.CHECK:
                acc.check += payment.amount;
                break;
            case PaymentMethodEnum.DEBT:
                acc.debt += payment.amount;
                break;
            case PaymentMethodEnum.BNPL:
            case PaymentMethodEnum.OTHER:
                acc.other += payment.amount;
                break;
        }
        return acc;
    }, { cash: 0, card: 0, ebt: 0, check: 0, debt: 0, other: 0 });
}
/**
 * Create a new payment transaction
 * Helper to ensure all required fields are present
 *
 * @param params - Payment transaction parameters
 * @returns Validated payment transaction
 */
export function createPaymentTx(params) {
    const basePayment = {
        ...params,
        date: params.date ?? new Date(),
    };
    if (params.method === PaymentMethodEnum.CARD ||
        params.method === PaymentMethodEnum.EBT ||
        params.method === PaymentMethodEnum.MANUAL_CARD ||
        params.method === PaymentMethodEnum.BLE_CONNECTED_READER ||
        params.method === PaymentMethodEnum.WEB_PAYMENT ||
        params.method === PaymentMethodEnum.RECURRING_PAYMENT ||
        params.method === PaymentMethodEnum.PAYMENT_LINK) {
        if (!params.cardDetails) {
            throw new Error('Card details required for card/EBT payments');
        }
        return CardPaymentTxSchema.parse({
            ...basePayment,
            cardDetails: params.cardDetails,
        });
    }
    return NonCardPaymentTxSchema.parse(basePayment);
}
